import type { Locales } from "@/i18n/i18n-types"

export class LanguageOption {
  private readonly localizedNameLowerCase?: string
  private readonly englishNameLowerCase: string

  private constructor(locale: Locales, englishName: string) {
    this.locale = locale
    this.localizedName = new Intl.DisplayNames([this.locale], { type: 'language' }).of(this.locale)
    this.englishName = englishName

    this.localizedNameLowerCase = this.localizedName?.toLowerCase()
    this.englishNameLowerCase = this.englishName.toLowerCase()
  }

  public readonly locale: Locales
  public readonly localizedName?: string
  public readonly englishName: string

  public get displayName() {
    return this.localizedName ?? this.englishName
  }

  public static readonly English = new LanguageOption('en', 'English')
  public static readonly Spanish = new LanguageOption('es', 'Spanish')
  public static readonly French = new LanguageOption('fr', 'French')
  public static readonly Chinese = new LanguageOption('zh', 'Chinese')

  public static Values() {
    return [
      LanguageOption.English,
      LanguageOption.Spanish,
      LanguageOption.French,
      LanguageOption.Chinese
    ]
  }

  public static FromLocale(locale: Locales): LanguageOption | undefined {
    return LanguageOption.Values().find(v => v.locale === locale)
  }

  public static FromName(name?: string): LanguageOption | undefined {
    if (!name) return undefined
    const lowerCaseName = name.toLowerCase()
    return LanguageOption.Values().find(v => v.localizedNameLowerCase === lowerCaseName || v.englishNameLowerCase === lowerCaseName)
  }

  public static Parse(value?: string): LanguageOption | undefined {
    return LanguageOption.FromLocale(value as Locales) ?? LanguageOption.FromName(value)
  }
}
